import { FormEvent, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { OrderSummary, SubmissionSummary } from "@shared/types";
import { formatAud } from "@shared/types";
import { formatOrderMoney, formatPaidSpend } from "@shared/sgdDiyPrices";
import {
  PLANT_SPECIES_ID,
  PLANT_SPECIES_ID_PHOTO_KINDS,
  countPlantPhotos,
  isPlantSpeciesIdSlug,
} from "@shared/plantSpeciesId";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DashboardShell } from "../components/Layout";

const clientNav = [
  { to: "/dashboard", label: "Overview" },
  { to: "/dashboard/library", label: "My library" },
  { to: "/dashboard/reports", label: "My reports" },
  { to: "/dashboard/billing", label: "Billing" },
  { to: "/dashboard/profile", label: "Profile" },
];

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <p className="container section muted">Loading…</p>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function DashboardHome() {
  const { user } = useAuth();
  const [subs, setSubs] = useState<SubmissionSummary[]>([]);
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [params] = useSearchParams();

  useEffect(() => {
    void Promise.all([api.submissions(), api.orders()]).then(([s, o]) => {
      setSubs(s.submissions);
      setOrders(o.orders);
    });
  }, []);

  return (
    <DashboardShell
      title={`Welcome, ${user?.fullName?.split(" ")[0] ?? "client"}`}
      subtitle="Manage downloads, guided reports and invoices."
      nav={clientNav}
    >
      {params.get("paid") ? (
        <div className="alert alert-success">Payment received. Thank you.</div>
      ) : null}
      <div className="stat-grid">
        <div className="stat">
          <div className="label">Reports</div>
          <div className="value">{subs.length}</div>
        </div>
        <div className="stat">
          <div className="label">In review / ready</div>
          <div className="value">
            {subs.filter((s) => s.status === "in_review" || s.status === "ready").length}
          </div>
        </div>
        <div className="stat">
          <div className="label">Paid orders</div>
          <div className="value">{orders.filter((o) => o.status === "paid").length}</div>
        </div>
        <div className="stat">
          <div className="label">Spend</div>
          <div className="value" style={{ fontSize: "1.15rem" }}>
            {formatPaidSpend(orders)}
          </div>
        </div>
      </div>
      <div className="grid-2">
        <div className="card">
          <h3>Quick actions</h3>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.75rem" }}>
            <Link className="btn btn-primary btn-sm" to="/resources">
              Browse resources
            </Link>
            <Link className="btn btn-outline btn-sm" to="/reports">
              New guided report
            </Link>
            <Link className="btn btn-outline btn-sm" to="/contact">
              Consultancy enquiry
            </Link>
          </div>
        </div>
        <div className="card">
          <h3>Recent reports</h3>
          {subs.slice(0, 4).map((s) => (
            <div key={s.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 8 }}>
              <Link to={`/dashboard/reports/${s.id}`}>
                {s.projectName || s.reportTypeName || s.id}
              </Link>
              <span className={`badge badge-status ${s.status}`}>{s.status.replace("_", " ")}</span>
            </div>
          ))}
          {!subs.length ? <p className="muted">No reports yet.</p> : null}
        </div>
      </div>
    </DashboardShell>
  );
}

export function DashboardLibrary() {
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    // purchases surface via resource download entitlement; list published resources as library entry points
  }, []);

  return (
    <DashboardShell
      title="My library"
      subtitle="Re-download purchased and free claimed resources."
      nav={clientNav}
    >
      {msg ? <div className="alert alert-info">{msg}</div> : null}
      <div className="card">
        <p>
          Open any resource from the{" "}
          <Link to="/resources">public library</Link>. Purchased and free items remain available for
          re-download while signed in.
        </p>
        <button
          className="btn btn-outline btn-sm"
          type="button"
          onClick={() => setMsg("Use the library pages to download entitled files.")}
        >
          How re-download works
        </button>
      </div>
    </DashboardShell>
  );
}

export function DashboardReports() {
  const [subs, setSubs] = useState<SubmissionSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .submissions()
      .then((r) => setSubs(r.submissions))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed"));
  }, []);

  return (
    <DashboardShell title="My reports" subtitle="Track Tier 2 guided report status." nav={clientNav}>
      {error ? <div className="alert alert-error">{error}</div> : null}
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Project</th>
              <th>Type</th>
              <th>Status</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.id}>
                <td>{s.projectName || "—"}</td>
                <td>{s.reportTypeName}</td>
                <td>
                  <span className={`badge badge-status ${s.status}`}>
                    {s.status.replace("_", " ")}
                  </span>
                </td>
                <td>{new Date(s.updatedAt).toLocaleDateString("en-AU")}</td>
                <td>
                  <Link className="btn btn-outline btn-sm" to={`/dashboard/reports/${s.id}`}>
                    Open
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!subs.length ? <p className="muted">No submissions yet. Start from Guided Reports.</p> : null}
    </DashboardShell>
  );
}

export function DashboardReportDetail() {
  const { id = "" } = useParams();
  const { user } = useAuth();
  const [sub, setSub] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const steps = sub?.reportType?.questionnaireSchema?.steps ?? [];
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [projectName, setProjectName] = useState("");

  async function reload() {
    const r = await api.submission(id);
    setSub(r.submission);
    setAnswers(r.submission.answers || {});
    setProjectName(r.submission.projectName || "");
  }

  const current = steps[stepIdx];
  const isDraft = sub?.status === "draft";
  const isStaff = user?.role === "admin" || user?.role === "reviewer";
  const plantId = isPlantSpeciesIdSlug(sub?.reportType?.slug);
  const photoCount = countPlantPhotos(sub?.files ?? []);

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : "Failed"));
  }, [id]);

  useEffect(() => {
    if (params.get("paid")) {
      setInfo(
        plantId
          ? "Payment recorded. Upload 3 photos if available, then submit."
          : "Payment recorded. You can submit when the questionnaire is complete."
      );
    }
  }, [params, plantId]);

  async function saveAnswers() {
    setBusy(true);
    setError(null);
    try {
      await api.updateSubmission(id, { projectName, answers });
      setInfo("Saved.");
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function pay() {
    setBusy(true);
    try {
      await saveAnswers();
      const res = await api.checkoutSubmission(id);
      window.location.href = res.checkoutUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Checkout failed");
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    try {
      await saveAnswers();
      await api.submitSubmission(id);
      setInfo("Submitted for review.");
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Submit failed");
    } finally {
      setBusy(false);
    }
  }

  async function onFile(file: File | null, kind = "client_upload") {
    if (!file) return;
    setBusy(true);
    try {
      await api.uploadSubmissionFile(id, file, kind);
      setInfo(`Uploaded ${file.name}`);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  if (!sub) {
    return (
      <DashboardShell title="Report" nav={clientNav}>
        <p className="muted">{error || "Loading…"}</p>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell
      title={sub.reportType?.name || (plantId ? PLANT_SPECIES_ID.name : "Guided report")}
      subtitle={`Status: ${sub.status.replace("_", " ")}`}
      nav={clientNav}
    >
      {error ? <div className="alert alert-error">{error}</div> : null}
      {info ? <div className="alert alert-success">{info}</div> : null}

      {isDraft ? (
        <>
          <label>
            Project name
            <input value={projectName} onChange={(e) => setProjectName(e.target.value)} />
          </label>

          <div className="steps">
            {steps.map((s: any, i: number) => (
              <button
                key={s.id}
                type="button"
                className={`step ${i === stepIdx ? "active" : ""} ${i < stepIdx ? "done" : ""}`}
                onClick={() => setStepIdx(i)}
              >
                {i + 1}. {s.title}
              </button>
            ))}
            <button
              type="button"
              className={`step ${stepIdx === steps.length ? "active" : ""}`}
              onClick={() => setStepIdx(steps.length)}
            >
              {plantId ? "Photos & submit" : "Files & submit"}
            </button>
          </div>

          {current ? (
            <div className="card">
              <h3>{current.title}</h3>
              {current.fields.map((f: any) => (
                <label key={f.id}>
                  {f.label}
                  {f.required ? " *" : ""}
                  {f.type === "textarea" ? (
                    <textarea
                      required={!!f.required}
                      value={String(answers[f.id] ?? "")}
                      onChange={(e) => setAnswers({ ...answers, [f.id]: e.target.value })}
                    />
                  ) : f.type === "select" ? (
                    <select
                      required={!!f.required}
                      value={String(answers[f.id] ?? "")}
                      onChange={(e) => setAnswers({ ...answers, [f.id]: e.target.value })}
                    >
                      <option value="">Select…</option>
                      {(f.options || []).map((o: string) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : f.type === "checkbox" ? (
                    <input
                      type="checkbox"
                      checked={Boolean(answers[f.id])}
                      onChange={(e) => setAnswers({ ...answers, [f.id]: e.target.checked })}
                    />
                  ) : (
                    <input
                      type={f.type === "number" ? "number" : "text"}
                      required={!!f.required}
                      value={String(answers[f.id] ?? "")}
                      onChange={(e) =>
                        setAnswers({
                          ...answers,
                          [f.id]: f.type === "number" ? Number(e.target.value) : e.target.value,
                        })
                      }
                    />
                  )}
                </label>
              ))}
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button
                  className="btn btn-outline btn-sm"
                  type="button"
                  disabled={stepIdx === 0}
                  onClick={() => setStepIdx((i) => i - 1)}
                >
                  Back
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  type="button"
                  onClick={() => {
                    void saveAnswers();
                    setStepIdx((i) => Math.min(i + 1, steps.length));
                  }}
                >
                  Save & continue
                </button>
              </div>
            </div>
          ) : (
            <div className="card">
              {plantId ? (
                <>
                  <h3>3 photos and payment</h3>
                  <p>{PLANT_SPECIES_ID.photoCopy}</p>
                  <p className="muted">{PLANT_SPECIES_ID.fewerCopy}</p>
                  {PLANT_SPECIES_ID_PHOTO_KINDS.map((kind, idx) => {
                    const uploaded = [...(sub.files ?? [])]
                      .reverse()
                      .find((f: { kind?: string }) => f.kind === kind);
                    return (
                      <label key={kind}>
                        Photo {idx + 1}
                        {idx === 0 ? " (required if you have any)" : " (preferred)"}
                        {uploaded ? (
                          <span className="muted"> {uploaded.file_name}</span>
                        ) : null}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => void onFile(e.target.files?.[0] ?? null, kind)}
                        />
                      </label>
                    );
                  })}
                  <p className="muted" style={{ fontSize: "0.88rem" }}>
                    {photoCount} of 3 photo slots filled. {PLANT_SPECIES_ID.deliveryCopy}
                  </p>
                </>
              ) : (
                <>
                  <h3>Files, logo & payment</h3>
                  <label>
                    Upload site files (PDF, images, office)
                    <input
                      type="file"
                      onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
                    />
                  </label>
                  <label>
                    Client logo (co-branding)
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => void onFile(e.target.files?.[0] ?? null, "logo")}
                    />
                  </label>
                </>
              )}
              {sub.files?.length ? (
                <ul>
                  {sub.files.map((f: any) => (
                    <li key={f.id}>
                      {f.file_name} <span className="muted">({f.kind})</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "1rem" }}>
                <button className="btn btn-outline" type="button" disabled={busy} onClick={() => void saveAnswers()}>
                  Save draft
                </button>
                {sub.reportType?.priceCents > 0 ? (
                  <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void pay()}>
                    Pay {formatAud(sub.reportType.priceCents)} {plantId ? "incl. GST" : "ex GST"}
                  </button>
                ) : null}
                <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void submit()}>
                  {plantId ? "Submit photos for ID" : "Submit for review"}
                </button>
              </div>
              <p className="muted" style={{ marginTop: "0.75rem", fontSize: "0.88rem" }}>
                {plantId
                  ? "Payment is required before submit. Identification is from the photos only."
                  : "Payment is required before submit for paid report types. Status flow: Submitted → In review → Ready for download."}
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="card">
          <p>
            <strong>Project:</strong> {sub.projectName || "—"}
          </p>
          <p>
            <strong>Status:</strong>{" "}
            <span className={`badge badge-status ${sub.status}`}>
              {sub.status.replace("_", " ")}
            </span>
          </p>
          {sub.status === "ready" ? (
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => void api.downloadSubmission(id, sub.finalFileName)}
            >
              Download deliverable
            </button>
          ) : (
            <p className="muted">
              {plantId
                ? `${PLANT_SPECIES_ID.deliveryCopy} Track this intake from your reports list.`
                : "Our team is processing your submission. Target ~48 hours."}
            </p>
          )}
          {isStaff ? (
            <p style={{ marginTop: "1rem" }}>
              <Link to={`/admin/submissions/${id}`}>Open in admin review →</Link>
            </p>
          ) : null}
          <button className="btn btn-outline btn-sm" type="button" style={{ marginTop: 12 }} onClick={() => navigate("/dashboard/reports")}>
            Back to list
          </button>
        </div>
      )}
    </DashboardShell>
  );
}

export function DashboardBilling() {
  const [orders, setOrders] = useState<OrderSummary[]>([]);

  useEffect(() => {
    void api.orders().then((r) => setOrders(r.orders));
  }, []);

  return (
    <DashboardShell title="Billing" subtitle="Payment history and tax invoices." nav={clientNav}>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Type</th>
              <th>Status</th>
              <th>Subtotal</th>
              <th>GST</th>
              <th>Total</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id}>
                <td>{o.invoiceNumber || o.id.slice(0, 12)}</td>
                <td>{o.orderType}</td>
                <td>{o.status}</td>
                <td>{formatOrderMoney(o.subtotalCents, o.currency)}</td>
                <td>{formatOrderMoney(o.gstCents, o.currency)}</td>
                <td>{formatOrderMoney(o.totalCents, o.currency)}</td>
                <td>{new Date(o.createdAt).toLocaleDateString("en-AU")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!orders.length ? <p className="muted">No payments yet.</p> : null}
    </DashboardShell>
  );
}

export function DashboardProfile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    fullName: user?.fullName ?? "",
    companyName: user?.companyName ?? "",
    phone: user?.phone ?? "",
    abn: user?.abn ?? "",
    password: "",
  });
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setForm({
        fullName: user.fullName,
        companyName: user.companyName ?? "",
        phone: user.phone ?? "",
        abn: user.abn ?? "",
        password: "",
      });
    }
  }, [user]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await api.updateMe({
        fullName: form.fullName,
        companyName: form.companyName || null,
        phone: form.phone || null,
        abn: form.abn || null,
        password: form.password || undefined,
      });
      setUser(res.user);
      setMsg("Profile updated.");
      setForm((f) => ({ ...f, password: "" }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    }
  }

  return (
    <DashboardShell title="Profile" subtitle="Company details and password." nav={clientNav}>
      <form className="card" style={{ maxWidth: 560 }} onSubmit={(e) => void onSubmit(e)}>
        {msg ? <div className="alert alert-success">{msg}</div> : null}
        {error ? <div className="alert alert-error">{error}</div> : null}
        <label>
          Full name
          <input
            required
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
          />
        </label>
        <label>
          Company
          <input
            value={form.companyName}
            onChange={(e) => setForm({ ...form, companyName: e.target.value })}
          />
        </label>
        <label>
          Phone
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </label>
        <label>
          ABN
          <input value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} />
        </label>
        <label>
          Set / change password
          <input
            type="password"
            minLength={8}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Leave blank to keep"
          />
        </label>
        <p className="muted" style={{ fontSize: "0.88rem" }}>
          Signed in as {user?.email}
        </p>
        <button className="btn btn-primary" type="submit">
          Save profile
        </button>
      </form>
    </DashboardShell>
  );
}
